import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionPlatform,
} from '@client/graphql/__generated__/types';
import {
  getContractDocDisplayName,
  getValidationResultsForDocument,
} from './validation.utils';

interface GenerateErrorMessageOptions {
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment;
  validationData?: ExternalValidationReportDataFragment | null;
  userAddedIssues?: IngestionDocumentValidationErrorReviewFragment[];
  hiddenIssues?: IngestionDocumentValidationErrorReviewFragment[];
  platform?: IngestionPlatform;
  includeDocumentName?: boolean;
  /**
   * Use compact format for platforms that don't handle newlines well (e.g., Skyslope)
   * Separates errors with commas instead of newlines
   */
  useCompactFormat?: boolean;
}

/**
 * Generates a friendly error message for validation issues
 * Similar to the messaging format in ComplianceMessagingPage
 */
export const generateValidationErrorMessage = ({
  document,
  validationData,
  userAddedIssues = [],
  hiddenIssues = [],
  includeDocumentName = true,
  useCompactFormat = false,
}: GenerateErrorMessageOptions): string => {
  // Get validation errors for the document
  const { errors: contractErrors } = getValidationResultsForDocument(
    document,
    validationData
  );

  // Filter out hidden validation errors
  const visibleValidationErrors = contractErrors.filter((error) => {
    return !hiddenIssues.some(
      (review) =>
        review.ruleUuid === error.ruleUuid &&
        review.contractDocumentUuid === error.contractDocumentUuid &&
        review.isHidden
    );
  });

  // Check if there are any errors to report
  if (visibleValidationErrors.length === 0 && userAddedIssues.length === 0) {
    return '';
  }

  // Collect all error messages
  const errorMessages: string[] = [];

  // Add user-added issues first (they're typically more important/custom)
  userAddedIssues.forEach((issue) => {
    if (issue.message) {
      errorMessages.push(issue.message);
    }
  });

  // Add validation errors
  visibleValidationErrors.forEach((error) => {
    if (error.message) {
      errorMessages.push(error.message);
    }
  });

  // Use compact format for platforms like Skyslope
  if (useCompactFormat) {
    let message = 'Please fix the following: ';
    message += errorMessages.join(' | ');
    message += '. Thanks!';

    return message;
  }

  // Use full format with newlines for platforms like Dotloop
  const sections: string[] = [];

  // Add friendly greeting
  sections.push('Please fix the following validation errors:');
  sections.push('');

  // Add document name header if requested
  if (includeDocumentName) {
    const contractDocName = document.contractDocuments?.[0]
      ? getContractDocDisplayName(document.contractDocuments[0])
      : document.documentName;
    sections.push(contractDocName);
  }

  // Add errors as bullet points
  errorMessages.forEach((msg) => {
    sections.push(`- ${msg}`);
  });

  // Add friendly closing
  sections.push('');
  sections.push('Thanks!');

  return sections.join('\n');
};
